import { makeStyles } from '@fluentui/react-components';
import { CalendarToday, Close, EventAvailable, EventBusy } from '@material-symbols-svg/react/rounded/w500';

import type { DayCell } from '../../calendar';
import { formatHM } from '../../common';
import { MONO_FONT, palette, tokens, useAppButtonStyles } from '../../theme';
import { DonutChart } from '../charts';

const formatDialogDate = (iso: string): string => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

const subtitle = (cell: DayCell, totalLogged: number, remaining: number): string => {
    if (cell.nonWorking) {
        switch (cell.nonWorking.kind) {
            case 'weekend':
                return 'Weekend';
            case 'holiday':
                return `Public holiday — ${cell.nonWorking.name}`;
            case 'dayOff':
                return 'Personal day off';
        }
    }
    return `${formatHM(totalLogged)} of ${formatHM(cell.capacity)} logged · ${formatHM(Math.max(0, remaining))} remaining`;
};

export type DialogHeaderProps = {
    cell: DayCell;
    logged: number;
    pending: number;
    onToggleDayOff?: () => void;
    onClose?: () => void;
};

export const DialogHeader: React.FC<DialogHeaderProps> = ({ cell, logged, pending, onToggleDayOff, onClose }) => {
    const classes = useStyles();
    const buttons = useAppButtonStyles();
    const totalLogged = logged + pending;
    const remaining = Math.max(0, cell.capacity - totalLogged);
    const isDayOff = cell.nonWorking?.kind === 'dayOff';
    const isReadOnly = cell.nonWorking != null;

    return (
        <div className={classes.root}>
            <div className={classes.titleRow}>
                <div className={classes.dateIcon} aria-hidden>
                    <CalendarToday className={classes.dateIconSvg} />
                </div>
                <div className={classes.titleColumn}>
                    <h2 className={classes.title}>{formatDialogDate(cell.date)}</h2>
                    <p className={classes.subtitle}>{subtitle(cell, totalLogged, remaining)}</p>
                </div>

                {!isReadOnly && (
                    <div className={classes.progressGroup}>
                        <CircularProgress logged={logged} pending={pending} capacity={cell.capacity} />
                        <Legend logged={logged} pending={pending} remaining={remaining} />
                    </div>
                )}

                <div className={classes.headerActions}>
                    {onToggleDayOff && (
                        <button type="button" className={buttons.secondary} onClick={() => onToggleDayOff()}>
                            {isDayOff ? <EventAvailable className={classes.dayOffIcon} /> : <EventBusy className={classes.dayOffIcon} />}
                            <span>{isDayOff ? 'Remove day off' : 'Mark day off'}</span>
                        </button>
                    )}
                    {onClose && (
                        <button type="button" className={classes.closeButton} aria-label="Close" onClick={onClose}>
                            <Close className={classes.closeIcon} aria-hidden />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

type CircularProgressProps = { logged: number; pending: number; capacity: number };

const CircularProgress: React.FC<CircularProgressProps> = ({ logged, pending, capacity }) => {
    const classes = useStyles();

    const total = capacity === 0 ? 1 : capacity;
    const safeLogged = Math.max(0, logged + Math.min(0, pending));
    const safePending = Math.max(0, pending);
    const totalHours = Math.max(0, logged + pending);

    const loggedFrac = Math.min(1, safeLogged / total);
    const pendingFrac = Math.min(1 - loggedFrac, safePending / total);

    return (
        <DonutChart
            size={64}
            strokeWidth={4}
            trackColor={palette.panelMuted}
            segments={[
                { fraction: loggedFrac, color: palette.goodAccent },
                { fraction: pendingFrac, color: palette.pendingAccent },
            ]}
        >
            <span className={classes.totalValue}>{formatHM(totalHours)}</span>
            <span className={classes.totalSuffix}>of {formatHM(capacity)}</span>
        </DonutChart>
    );
};

type LegendProps = { logged: number; pending: number; remaining: number };

const Legend: React.FC<LegendProps> = ({ logged, pending, remaining }) => {
    const classes = useStyles();
    return (
        <div className={classes.legend}>
            <LegendRow filled color={palette.goodAccent} label="Logged" value={formatHM(logged)} />
            <LegendRow filled color={palette.pendingAccent} label="Pending" value={pending === 0 ? '—' : formatHM(pending)} />
            <LegendRow filled={false} color={palette.mutedAccent} label="Remaining" value={remaining > 0 ? formatHM(remaining) : '—'} />
        </div>
    );
};

type LegendRowProps = { filled: boolean; color: string; label: string; value: string };

const LegendRow: React.FC<LegendRowProps> = ({ filled, color, label, value }) => {
    const classes = useStyles();
    return (
        <div className={classes.legendItem}>
            <span
                className={classes.legendDot}
                style={{
                    backgroundColor: filled ? color : palette.panelBg,
                    border: `1px solid ${color}`,
                }}
            />
            <span className={classes.legendLabel}>{label}</span>
            <span className={classes.legendValue}>{value}</span>
        </div>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    dateIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        backgroundColor: palette.accentBg,
        color: palette.accentText,
        flexShrink: 0,
    },
    dateIconSvg: {
        width: '22px',
        height: '22px',
    },
    titleColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: 0,
    },
    title: {
        margin: 0,
        fontSize: '20px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
        lineHeight: 1.2,
    },
    subtitle: {
        margin: 0,
        fontSize: '13px',
        color: tokens.colorNeutralForeground2,
    },
    progressGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalL,
        marginLeft: tokens.spacingHorizontalXXL,
    },
    headerActions: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        marginLeft: 'auto',
        flexShrink: 0,
    },
    dayOffIcon: {
        width: '16px',
        height: '16px',
    },
    closeButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        background: 'transparent',
        border: 'none',
        color: palette.dimText,
        borderRadius: '50%',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background-color 160ms ease, color 160ms ease',
        '&:hover': {
            backgroundColor: palette.panelMuted,
            color: tokens.colorNeutralForeground1,
        },
        '&:focus-visible': {
            outline: `2px solid ${palette.accent}`,
            outlineOffset: '2px',
        },
    },
    closeIcon: {
        width: '20px',
        height: '20px',
    },
    totalValue: {
        fontFamily: MONO_FONT,
        fontSize: '14px',
        fontWeight: tokens.fontWeightSemibold,
        color: palette.loggedText,
        lineHeight: 1,
    },
    totalSuffix: {
        fontFamily: MONO_FONT,
        fontSize: '10px',
        color: tokens.colorNeutralForeground3,
        lineHeight: 1,
    },
    legend: {
        display: 'grid',
        gridTemplateColumns: 'auto auto auto',
        columnGap: '10px',
        rowGap: '3px',
        alignItems: 'center',
    },
    legendItem: {
        display: 'contents',
    },
    legendDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        boxSizing: 'border-box',
    },
    legendLabel: {
        fontSize: '12px',
        color: palette.dimText,
    },
    legendValue: {
        fontFamily: MONO_FONT,
        fontSize: '12px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'right',
        justifySelf: 'end',
    },
});
